import React, {useEffect, useState} from "react";
import {LoadingPanel} from "../../../controls/vidence/blanks/LoadingPanel";
import {Button} from "../../../controls/tailwindui/button";
import {useGuideFlowProviderContext} from "../../../../GuideFlowProviderContext";
import {Label, Field} from "../../../controls/tailwindui/fieldset";
import {Input} from "../../../controls/tailwindui/input";
import {FluentCrmConfig} from "../../../../providers/fluentCRM/FluentCrmProvider";
import {BulkContactSyncReport} from "../../Contact/FluentCrm/BulkContactSyncReport";
import {BulkSyncResult, BulkSyncSummary, FluentCRMTag} from "../../../../providers/fluentCRM/FluentCRMTypes";
import {RevealTextbox} from "../../../controls/gf/RevealTextbox";
import {FluentCrmSynchronizer} from "../../../../providers/fluentCRM/FluentCrmSynchronizer";
import {BulkTagSyncReport} from "../../Contact/FluentCrm/BulkTagSyncReport";


export default function FluentCrmTab() {
    return (
        <div className="p-3 mx-auto max-w-2xl sm:space-y-5 lg:mx-0 lg:max-w-none">
            <FluentCrmIntegrationConfig/>
            <hr className="my-3"/>
            <FluentCrmSync/>
        </div>
    )
}


function FluentCrmIntegrationConfig() {
    const [websiteUrl, setWebsiteUrl] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    const gfContext = useGuideFlowProviderContext();

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const config = await gfContext.FluentCrmProvider.getConfig();
                if (config) {
                    setWebsiteUrl(config.websiteUrl);
                    setApiKey(config.apiKey);
                }
            } catch (err: any) {
                setError("Failed to load FluentCRM configuration.");
            } finally {
                setLoaded(true);
            }
        };
        loadConfig();
    }, [gfContext.FluentCrmProvider, gfContext.FluentCrmProvider.StateChanged]);

    const testFluentIntegration = async () => {
        gfContext.FluentCrmProvider.getContacts().then(contacts => {
            alert("Connection Test: Success: " + contacts.total + " total contacts reported");
        }).catch(err => {
            alert("Failed with message: " + err.message + "")
        });
    }

    const saveFluentIntegration = async () => {
        setError(null);
        setSuccess(null);

        // Basic validation
        if (!websiteUrl || !websiteUrl.startsWith("http")) {
            setError("Please enter a valid website URL (must start with http or https).");
            return;
        }

        if (!apiKey || apiKey.length < 10) {
            setError("Please enter a valid API key.");
            return;
        }

        try {
            await gfContext.FluentCrmProvider.saveConfig(websiteUrl, apiKey);
            setSuccess("FluentCRM integration settings saved successfully!");
        } catch (err: any) {
            setError(
                err?.message || "An error occurred while saving FluentCRM configuration."
            );
        }
    };

    if (!loaded) {
        return <LoadingPanel text="Loading fluent crm configuration..."/>;
    }

    return (
        <div className="sm:space-y-5">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
                Fluent CRM Integration
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
                Settings for integrating with{" "}
                <a
                    href="https://fluentcrm.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                >
                    Fluent CRM
                </a>
                ; a WordPress plugin
            </p>
            <p className="mt-1 text-sm leading-6 text-gray-500">
                This integration will synchronize your clients and their healings to
                Fluent CRM so you can engage with marketing and email automation
            </p>
            <ul className="mt-1 text-sm leading-6 text-gray-500 list-disc list-inside">
                <li>Healings are tags under their name (i.e. life activation is a tag)</li>
                <li>
                    Lists vary but generally are "All initiates", "Clients ready for
                    Initiation", etc.
                </li>
            </ul>

            {/* Website URL field */}
            <div className="mt-4">
                <Field>
                    <Label htmlFor="fluentcrm-url">Website URL</Label>
                    <Input
                        id="fluentcrm-url"
                        name="fluentcrm-url"
                        type="url"
                        placeholder="https://yourwordpresssite.com"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                </Field>
            </div>

            {/* API Key field */}
            <div className="mt-4">
                <Field>
                    <Label htmlFor="fluentcrm-api-key">API Key</Label>
                    <RevealTextbox value={apiKey} onChange={setApiKey}/>
                </Field>
            </div>

            {/* Error / Success messages */}
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            {success && <p className="mt-2 text-sm text-green-600">{success}</p>}

            <Button className="mt-6" onClick={saveFluentIntegration}>
                Save
            </Button>

            <Button
                disabled={(!(websiteUrl && apiKey))}
                className="ml-3 mt-6" onClick={testFluentIntegration}>
                Test
            </Button>
        </div>
    );
}

type DefinitionSyncResults = {
    created: FluentCRMTag[];
    existing: FluentCRMTag[];
    failed: { title: string; error: string }[];
};

function FluentCrmSync() {
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [config, setConfig] = useState<FluentCrmConfig | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [contactSyncResults, setContactSyncResults] = useState<{
        result: BulkSyncResult,
        summary: BulkSyncSummary
    } | null>(null);

    const [healingTagResults, setHealingTagResults] = useState<DefinitionSyncResults | null>(null);
    const [classTagResults, setClassTagResults] = useState<DefinitionSyncResults | null>(null);


    const [workingState, setWorkingState] = useState<"prompt" | "working" | "work-complete">("prompt");
    const [workingMessage, setWorkingMessage] = useState<string>("");
    const gfContext = useGuideFlowProviderContext();

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const config = await gfContext.FluentCrmProvider.getConfig();
                setConfig(config);
            } catch (err: any) {
                setError("Failed to load FluentCRM configuration.");
            } finally {
                setLoaded(true);
            }
        };
        loadConfig();
    }, [gfContext.FluentCrmProvider, gfContext.FluentCrmProvider.StateChanged]);

    const syncAllDataNow = async () => {
        
        if (!config) {
            console.info("No config found, cannot sync.");
            return;
        }

        const crm = gfContext.FluentCrmProvider;
        const synchronizer = new FluentCrmSynchronizer(crm);

        setWorkingState("working");
        setWorkingMessage(`Getting your contacts...`);
        const contacts = await gfContext.Contacts.getAll();
        setWorkingMessage(`Copying ${contacts.length} contact(s) to your website at ${config.websiteUrl}; this might take some time.  Let's pull in some that good ol' Binah patience, hey?`);
        setError(null);
        setSuccess(null);

        // push up the contacts
        // try {
        //     const results = await synchronizer.bulkSyncAllContacts(contacts, setWorkingMessage);
        //     setContactSyncResults(results);
        //     setWorkingMessage(`Contact Sync complete!`);
        // } catch (err: any) {
        //     setError(err?.message + " <-here" || "An error occurred while saving FluentCRM configuration.");
        // }

        // create a tag for each healing
        setWorkingMessage(`Getting your healings...`);
        const healings = await gfContext.HealingDefinitions.getAll();
        setWorkingMessage(`Found ${healings.length} healings...`);

        const healingSyncResult = await synchronizer.syncHealingDefinitionsWithTags(
            healings,
            async (healing) => await gfContext.HealingDefinitions.save(healing),
            (msg: string) => setWorkingMessage(msg)
        );
        setHealingTagResults(healingSyncResult);


        setWorkingMessage(`Getting your classes...`);
        const classes = await gfContext.ClassDefinitions.getAll();

        const classSyncResult = await synchronizer.syncClassDefinitionsWithTags(
            classes,
            async (cls) => await gfContext.ClassDefinitions.save(cls),
            (msg: string) => setWorkingMessage(msg)
        );
        setClassTagResults(classSyncResult);

        setWorkingMessage(`done...`);

        setWorkingState("work-complete");
        // create a tag for each class attended

    };

    if (!loaded) {
        return <LoadingPanel text="Loading fluent crm configuration..."/>;
    }

    if (workingState === "working") {
        return (<div className="p-3 mx-auto max-w-2xl sm:space-y-5 lg:mx-0 lg:max-w-none">
            <LoadingPanel text={workingMessage}/>
        </div>);
    }

    if (workingState === "work-complete" && contactSyncResults) {
        return <div className="p-3 mx-auto max-w-2xl sm:space-y-5 lg:mx-0 lg:max-w-none">
            <BulkContactSyncReport {...contactSyncResults}/>

            <hr/>
            <BulkTagSyncReport {...healingTagResults} />

            <hr/>
            <BulkTagSyncReport {...classTagResults} />

            <hr/>
            <Button className="mt-6" onClick={() => setWorkingState("prompt")}>
                Done
            </Button>
        </div>
    }

    return (
        <div className="sm:space-y-5">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
                Fluent CRM Resync Data
            </h2>

            <p className="mt-1 text-sm leading-6 text-gray-500">
                This will sync
            </p>
            <ul className="mt-1 text-sm leading-6 text-gray-500 list-disc list-inside">
                <li>Healings are tags under their name (i.e. life activation is a tag)</li>
                <li>
                    Lists vary but generally are "All initiates", "Clients ready for
                    Initiation", etc.
                </li>
            </ul>


            {/* Error / Success messages */}
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            {success && <p className="mt-2 text-sm text-green-600">{success}</p>}

            <Button className="mt-6" onClick={syncAllDataNow}>
                Sync
            </Button>
        </div>
    );
}

